import { useState } from "react";

import { Delete, MarkEmailRead } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "constants/index";

import { NotificationCard } from "../NotificationCard";
import { NotificationFilterMenu } from "./NotificationFilterMenu";
import { NotificationFilterTabs } from "./NotificationFilterTabs";
import {
  filterNotifications,
  getStoredNotificationFilter,
  getTruncationMessage,
  NOTIFICATION_FILTER_OPTIONS,
  storeNotificationFilter,
} from "./utils";

import type { Theme } from "@mui/material";

import type { NotificationFilter } from "./types";

interface Props {
  open: boolean;
  onClose: VoidFunction;
  notifications: Notification[];
  counts: Record<NotificationFilter, number>;
  loading: boolean;
  error: null | string;
  onMarkAllAsRead: VoidFunction;
  onDeleteAll: VoidFunction;
  onMarkNotifictionAsRead: (notification: Notification) => Promise<void>;
  onMarkNotifictionAsDeleted: (notification: Notification) => void;
  isUpdating: boolean;
}

export function NotificationModal(props: Props) {
  const {
    open,
    onClose,
    notifications,
    counts,
    loading,
    error,
    onMarkAllAsRead,
    onDeleteAll,
    onMarkNotifictionAsRead,
    onMarkNotifictionAsDeleted,
    isUpdating,
  } = props;
  const posthog = usePostHog();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const [filter, setFilter] = useState<NotificationFilter>(
    getStoredNotificationFilter,
  );

  const filteredNotifications = filterNotifications({ notifications, filter });

  const handleFilterChange = (selectedFilter: NotificationFilter) => {
    if (filter === selectedFilter) return;
    setFilter(selectedFilter);
    storeNotificationFilter({ filter: selectedFilter });
    posthog?.capture(ClientAppEvent.NOTIFICATION_FILTER_CHANGED, {
      filter: selectedFilter,
    });
  };

  const truncationMessage = getTruncationMessage({ filter, counts });

  const getModalContent = () => {
    if (loading || isUpdating) {
      return (
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      );
    }
    if (error != null) {
      return (
        <Stack>
          <Typography color="red" align="center">
            {error}
          </Typography>
        </Stack>
      );
    }
    if (filteredNotifications.length === 0) {
      return (
        <Typography sx={{ ml: "2rem" }} variant="body1" textAlign="center">
          {NOTIFICATION_FILTER_OPTIONS[filter].emptyMessage}
        </Typography>
      );
    }
    // The backend returns unread first, then read, each most recent first.
    return (
      <>
        {filteredNotifications.map((notification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            onClose={onClose}
            onMarkNotifictionAsRead={onMarkNotifictionAsRead}
            onMarkNotifictionAsDeleted={onMarkNotifictionAsDeleted}
          />
        ))}
        {truncationMessage != null && (
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            sx={{ pt: "0.5rem" }}
          >
            {truncationMessage}
          </Typography>
        )}
      </>
    );
  };

  const bulkActionWrapperStyle = isMobile
    ? { display: "block", marginTop: "0.5rem" }
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{ m: "1rem" }}
      // Always full height (capped by the Dialog's own max-height) so switching
      // tabs, empty tabs and loading states never resize the modal.
      slotProps={{ paper: { sx: { height: "100%" } } }}
    >
      <DialogTitle sx={{ fontSize: "1.5rem" }}>
        Notifications
        <Tooltip title="Mark all of your notifications as read, regardless of the selected filter">
          <span style={bulkActionWrapperStyle}>
            <Button
              disabled={notifications.length < 1}
              fullWidth={isMobile}
              sx={{ mx: isMobile ? 0 : "2rem" }}
              onClick={onMarkAllAsRead}
              variant="contained"
              color="warning"
              endIcon={<MarkEmailRead />}
            >
              <Typography fontSize="0.8rem">Mark all as read</Typography>
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Delete all of your notifications, regardless of the selected filter">
          <span style={bulkActionWrapperStyle}>
            <Button
              disabled={notifications.length < 1}
              fullWidth={isMobile}
              variant="contained"
              color="error"
              onClick={onDeleteAll}
              endIcon={<Delete />}
            >
              <Typography fontSize="0.8rem">Delete all</Typography>
            </Button>
          </span>
        </Tooltip>
      </DialogTitle>
      <Stack
        sx={{
          px: "1.5rem",
          pb: "0.5rem",
          alignItems: "flex-start",
          flexShrink: 0,
          overflowY: "hidden",
          scrollbarGutter: "stable",
        }}
      >
        {isMobile ? (
          <NotificationFilterMenu
            filter={filter}
            onFilterChange={handleFilterChange}
            counts={counts}
          />
        ) : (
          <NotificationFilterTabs
            filter={filter}
            onFilterChange={handleFilterChange}
            counts={counts}
          />
        )}
      </Stack>
      <DialogContent sx={{ pt: 0, scrollbarGutter: "stable" }}>
        <Stack>{getModalContent()}</Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
